import { fetchClinicStudiesSupport } from '@/entities/study';
import { handleSubmitSupportRequest } from '@/entities/support';
import { INVALID_EMAIL_ERROR_MESSAGE, LOADING_TYPES } from '@/shared/config';
import { isNumberByRegex, validateEmail, showMessageError, showMessageSuccess } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect } from 'react';
import { t } from 'ttag';
import type { AbortStudyProps, AbortStudyState } from './abortStudy.types';

/**
 * Builds a study id tag for the abort-study request's multi-value select. Uses the typed label as both label and value and starts with an empty errorMessage and an undefined study id, which validation fills in later.
 *
 * @param label - Study friendly id typed by the user.
 * @returns A study id tag option.
 */
const createOption = (label: any): any => ({
  label,
  value: label,
  errorMessage: '',
  id: undefined,
});

/**
 * Holds the form state of the abort-study support request: study id tags, reply emails, message
 * and errors. Validates each tagged study id against the facility's studies, clears the form and
 * revalidates the tags when the facility changes, and submits one content entry per study through
 * handleSubmitSupportRequest.
 *
 * @param props - Form props: category, selectedFacility, setLoadingPage and backToPrevious.
 * @returns An object carrying the form state and the change, send, email, paste, study id input,
 *   tag, key-down and click-outside handlers.
 */
export const useAbortStudy = ({
  category,
  selectedFacility,
  setLoadingPage,
  backToPrevious,
}: AbortStudyProps) => {
  const [state, setState] = useMergeState<AbortStudyState>({
    emailList: [],
    emailInputValue: '',
    message: '',
    errorObject: {},
    isSaving: false,
    multiSearchValue: [],
    inputValue: '',
  });

  /**
   * Stores a field of the abort-study request form, such as the message body. The value is written under the supplied field name and that field's entry is dropped from the error object.
   *
   * @param name - Field name used as the state key.
   * @param value - Value to store.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject[name];
    setState({ [name]: value, errorObject: cloneErrorObject });
  };

  /**
   * Looks up one clinic study by its friendly id so an abort-study request can be validated. Queries the support study list with includeRma and the selected facility and returns the first match; a failure is logged, the saving flag is cleared and an empty object is returned.
   *
   * @param studyFid - Friendly study id to look up.
   * @returns The matching study record, or an empty object on failure.
   */
  const fetchStudyId = async (studyFid: any) => {
    try {
      const queryFilter = {
        studyFid: studyFid ? Number(studyFid) : undefined,
        includeRma: true,
        facilityId: selectedFacility?.value,
        viewAll: true,
      };
      const result = await fetchClinicStudiesSupport(queryFilter, 1);
      return result[0];
    } catch (error) {
      console.error('Failed to check study ID: ', error);
      setState({ isSaving: false });
      return {};
    }
  };

  /**
   * Validates every study id tag entered on the abort-study request and annotates each one with its error state. Fetches the studies in parallel, matches them by friendlyId, then stamps each tag with its study id, with an open-request message when the study already has a clinicTicket, or with an invalid-study-id message when nothing matched, writing the annotated tags back to state.
   *
   * @param multiSearchValue - Study id tags entered so far.
   * @returns A promise resolving once the tag list and the saving flag have been updated in state.
   */
  const checkStudy = async (multiSearchValue: any) => {
    if (!_.isEmpty(multiSearchValue)) {
      setState({ isSaving: true });
      const clonedMultiSearchValue = _.cloneDeep(multiSearchValue);
      const promises: any = [];
      _.forEach(clonedMultiSearchValue, (item) => {
        promises.push(fetchStudyId(Number(item.value)));
      });
      const studyInfoArray: any = await Promise.all(promises);
      _.forEach(clonedMultiSearchValue, (item) => {
        /**
         * The fetched study whose friendlyId matches the entered tag value. Located in the fetched study array with lodash find using numeric comparison.
         *
         * @returns The matching study record, or undefined.
         */
        const foundItem = _.find(
          studyInfoArray,
          (x) => Number(x?.friendlyId) === Number(item?.value),
        );
        if (foundItem) {
          if (foundItem.clinicTicket?.id) {
            _.assign(item, {
              errorMessage: 'This study currently has an open request!',
              id: foundItem?.id,
            });
          } else {
            _.assign(item, { errorMessage: '', id: foundItem?.id });
          }
        } else {
          _.assign(item, { errorMessage: 'Invalid study ID', id: undefined });
        }
      });
      setState({ multiSearchValue: clonedMultiSearchValue, isSaving: false });
    }
  };

  /**
   * Submits the abort-study support request for the tagged studies. A malformed pending reply email is rejected with an inline error first, otherwise the submitting loader is shown and the request is posted with the category, joined reply emails, one content entry per tagged study id, the trimmed message and the selected facility, followed by a success toast and a return to the previous page; failures log, show an error toast and clear the saving flag.
   *
   * @returns A promise resolving once the toast feedback and the navigation have been triggered.
   */
  const onClickSendMessage = async () => {
    setState({ isSaving: true });
    const errorObject = {};
    const isValidCurrentInput = !state.emailInputValue || validateEmail(state.emailInputValue);
    if (!isValidCurrentInput) {
      _.assign(errorObject, { invalidEmails: INVALID_EMAIL_ERROR_MESSAGE });
    }
    if (!_.isEmpty(errorObject)) {
      setState({
        errorObject,
        isSaving: false,
      });
    } else {
      try {
        setLoadingPage(LOADING_TYPES.SUBMITTING);
        await handleSubmitSupportRequest({
          category,
          replyEmail: _.join(state.emailList, ','),
          contents: _.map(state.multiSearchValue, (item) => ({
            study: item?.id,
          })),
          message: state.message.trim(),
          appName: 'Bioflux',
          facilityId: selectedFacility?.value as string,
        });
        showMessageSuccess({
          content: t`We have received your request and will email you back when it has been completed`,
        });
        setLoadingPage(false);
        backToPrevious();
      } catch (error) {
        console.error('Failed to send support email: ', error);
        showMessageError({ content: t`Failed to send your message` });
        setState({
          isSaving: false,
        });
      }
    }
  };

  /**
   * Replaces the reply email list of the abort-study request with the addresses accepted by the multi-email input. Clears the pending email text and the invalid-email error at the same time.
   *
   * @param value - Accepted reply email addresses.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailList: value, emailInputValue: '', errorObject: cloneErrorObject });
  };

  /**
   * Tracks the reply email text typed but not yet committed on the abort-study request. Stores it as emailInputValue and clears the invalid-email error.
   *
   * @param value - Current text in the email input.
   * @returns Nothing.
   */
  const onInputChange = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailInputValue: value, errorObject: cloneErrorObject });
  };

  /**
   * Accepts a pasted block of study ids on the abort-study request. Splits the clipboard text on spaces and commas, keeps only numeric ids that are not already tagged, deduplicates them into options, appends them to the tag list and validates the result.
   *
   * @param e - Paste event carrying the clipboard text.
   * @returns Nothing.
   */
  const handleOnPaste = (e: any) => {
    e.preventDefault();
    const splitIds = e.clipboardData.getData('text').split(' ').join(',').split(',');
    /**
     * The pasted ids that are numeric and not already present in the tag list. Produced by lodash filter over the split clipboard values using isNumberByRegex.
     *
     * @returns The accepted study id strings from the paste.
     */
    const filteredData = _.filter(
      splitIds,
      (id) => isNumberByRegex(id) && !_.find(state.multiSearchValue, (x) => x.value === id),
    );
    /**
     * Select options built from the unique numeric study ids extracted from the pasted text. Each id is passed through createOption to get a label/value tag.
     *
     * @returns Study id options created from the paste.
     */
    const valueArr = _.map(_.uniq(filteredData), (item) => createOption(item));
    const newMultiSearchValue = [...state.multiSearchValue, ...valueArr];
    setState({ multiSearchValue: newMultiSearchValue, inputValue: '' });
    checkStudy(newMultiSearchValue);
  };

  /**
   * Keeps the abort-study id input restricted to digits while typing. A non-empty value failing the numeric regex is discarded, anything else is stored as inputValue.
   *
   * @param inputValue - Text typed into the study id select.
   * @returns Nothing.
   */
  const onInputChangeMultiIdSearch = (inputValue: any) => {
    if (inputValue !== '' && !isNumberByRegex(inputValue)) {
      return;
    }
    setState({ inputValue });
  };

  /**
   * Stores the study id tags of the abort-study request after the select changes, including when tags are cleared. A null value is replaced by an empty array so the tag list stays iterable.
   *
   * @param value - Tag list reported by the select, possibly null.
   * @returns Nothing.
   */
  const onChangeMultiIdSearch = (value: any) => {
    //* Trigger when clear tag
    //* Prevent null value so that multiSearchValue can be iterable
    setState({ multiSearchValue: value || [] });
  };

  /**
   * Turns the typed study id into a tag when comma or space is pressed in the abort-study id select. An empty input is ignored, the default key handling is prevented, and an id that is not yet tagged is appended as an option and validated, otherwise only the input is cleared.
   *
   * @param e - Keyboard event from the creatable select.
   * @returns Nothing.
   */
  const onKeyDownCreatableSelect = (e: any) => {
    if (!state.inputValue) {
      return;
    }
    if (e.keyCode === 188 || e.keyCode === 32) {
      e.preventDefault();
      if (!_.find(state.multiSearchValue, (x) => Number(x.value) === Number(state.inputValue))) {
        const value = createOption(state.inputValue);
        const newMultiSearchValue = [...state.multiSearchValue, value];
        setState({ multiSearchValue: newMultiSearchValue, inputValue: '' });
        checkStudy(newMultiSearchValue);
      } else {
        setState({ inputValue: '' });
      }
    }
  };

  /**
   * Commits the study id still sitting in the abort-study input when focus leaves the select. A non-empty id that is not already tagged becomes an option, is appended to the tag list and validated, otherwise only the input text is cleared.
   *
   * @returns Nothing.
   */
  const handleClickOutside = () => {
    if (
      !_.find(state.multiSearchValue, (x) => Number(x.value) === Number(state.inputValue)) &&
      state.inputValue !== ''
    ) {
      const value = createOption(state.inputValue);
      const newMultiSearchValue = [...state.multiSearchValue, value];
      setState({ multiSearchValue: newMultiSearchValue, inputValue: '' });
      checkStudy(newMultiSearchValue);
    } else {
      setState({ inputValue: '' });
    }
  };

  useEffect(() => {
    setState({
      emailList: [],
      emailInputValue: '',
      message: '',
      errorObject: {},
    });
    checkStudy(state.multiSearchValue);
  }, [selectedFacility]);

  return {
    state,
    onChange,
    onClickSendMessage,
    onChangeEmailList,
    onInputChange,
    handleOnPaste,
    onInputChangeMultiIdSearch,
    onChangeMultiIdSearch,
    onKeyDownCreatableSelect,
    handleClickOutside,
  };
};
